<template>
    <CardGenerate 
        ref="card" 
        class="card-generate-texture"
        :app="app"
        :card="card" 
        :actionable="false" 
        @init="handleInit">

        <template #title>
            Select your texture generation.
        </template>

        <template v-if="isOutage || isDegraded" #status>
            <AtomStatus 
                :level="statusLevel"
                :status="statusMessage" />
        </template>

        <template #pre>
            <div class="card-generate-texture-pre">
                <LayoutRowEntry class="entry" :centered="true">
                    <template #first>
                        <span>Transparency</span>
                    </template>
                    <template #second>  
                        <v-btn-toggle 
                            mandatory 
                            class="toggle" 
                            color="color1" 
                            elevation="0" 
                            density="default"
                            v-model="card.custom.transparency">
                            <v-btn 
                                class="toggle-button" 
                                elevation="0"
                                :value="keyTransparencyAllowed"  
                                @click="handleClick" 
                                @mouseenter="handleHover({id: keyTransparencyAllowed})" 
                                @mouseleave="handleHover(null)">
                                <v-icon icon="fa-solid fa-check"></v-icon>
                                <span>Allowed</span>
                            </v-btn>
                            <v-btn 
                                class="toggle-button" 
                                elevation="0"
                                :value="keyTransparencyDisabled"  
                                @click="handleClick" 
                                @mouseenter="handleHover({id: keyTransparencyDisabled})" 
                                @mouseleave="handleHover(null)">
                                <v-icon icon="fa-solid fa-xmark"></v-icon>
                                <span>Disabled</span>
                            </v-btn>
                        </v-btn-toggle>
                    </template>
                </LayoutRowEntry>
                <LayoutRowEntry class="entry" :centered="true">
                    <template #first>
                        <span>Projections</span>
                    </template>
                    <template #second>
                        <v-btn-toggle 
                            mandatory 
                            class="toggle" 
                            color="color1" 
                            elevation="0" 
                            density="default"
                            v-model="card.custom.projections" 
                            :disabled="!isHealthyProject && !isAdministrator">
                            <v-btn 
                                class="toggle-button" 
                                elevation="0"
                                :value="keyProjectionsAutomatic" 
                                :disabled="!hasProjectionsAutomatic || (isOutage && !isAdministrator)" 
                                @click="handleAutomatic" 
                                @mouseenter="handleHover({id: keyProjectionsAutomatic})" 
                                @mouseleave="handleHover(null)"> 
                                <v-icon icon="fa-solid fa-robot"></v-icon>
                                <span>Automatic</span>
                            </v-btn>
                            <v-btn 
                                class="toggle-button" 
                                elevation="0"
                                :value="keyProjectionsManual" 
                                :disabled="!hasProjectionsManual || (isOutage && !isAdministrator)" 
                                @click="handleManual" 
                                @mouseenter="handleHover({id: keyProjectionsManual})" 
                                @mouseleave="handleHover(null)"> 
                                <v-icon icon="fa-solid fa-user-gear"></v-icon>
                                <span>Manual</span>
                            </v-btn>
                        </v-btn-toggle>
                    </template>
                </LayoutRowEntry>
            </div>
        </template>

        <template #options>
            <OptionImage 
                v-for="option in options"
                :id="option.id"
                :index="option.index"
                :data="option.url"
                :label="option.label" 
                :beta="option.beta" 
                :compact="isExpertMode" 
                :disabled="(isOutage || !hasPrivileges) && !isAdministrator"
                :selected="selected === option.id"
                :selectable="option.selectable"
                @click="handleOption(option)"
                @mouseenter="handleHover(option)"
                @mouseleave="handleHover(null)">
            </OptionImage>
        </template>

        <template #post>
            <AtomText class="hide-on-mobile" :text="guidance" />
        </template>

        <template v-if="isExpertMode" #side>

            <SidePanelExpert>

                <SideTitle title="Mapping" />

                <div class="expert-item">
                    <v-select
                        label="algorithm" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked" 
                        :items="[
                            keyAlgorithmGenerio, 
                            keyAlgorithmXatlas
                        ]" 
                        v-model="card.custom.algo">
                    </v-select>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="merge distance" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        :precision="4" 
                        :rules="[
                            (v) => v >= 0,
                            (v) => v < 0.1
                        ]"
                        v-model="card.custom.mergeDistance">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="face limit" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        :rules="[
                            (v) => v < 1_000_000
                        ]"
                        v-model="card.custom.faceLimit">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-select
                        label="debug material" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked" 
                        :items="[
                            keyDebugMaterialNone,
                            keyDebugMaterialUV,
                            keyDebugMaterialStretch,
                            keyDebugMaterialCheckerboard,
                        ]" 
                        v-model="card.custom.debugMaterial">
                    </v-select>
                </div>

                <SideTitle title="Texture" />

                <div class="expert-item">
                    <v-select
                        label="multi-image strategy" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked" 
                        item-title="label"
                        item-value="value"
                        :items="keyStrategies" 
                        v-model="card.custom.multiImageStrategy">
                    </v-select>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="voxel resolution" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        :rules="[
                            (v) => v == 512 || v == 1024
                        ]"
                        v-model="card.custom.voxelResolution">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="texture resolution" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        :rules="[
                            (v) => v >= 256,
                            (v) => v <= 4096,
                        ]"
                        v-model="card.custom.textureResolution">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="steps" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        v-model="card.custom.textureSteps">
                    </v-number-input>
                </div>

                <div class="expert-item" style="display:flex;flex-direction:row;gap:10px;">
                    <v-number-input
                        label="min" 
                        color="color1" 
                        variant="outlined" 
                        control-variant="hidden" 
                        :precision="1" 
                        v-model="card.custom.textureIntervalMin">
                    </v-number-input>
                    <v-number-input
                        label="max" 
                        color="color1" 
                        variant="outlined" 
                        control-variant="hidden" 
                        :precision="1" 
                        v-model="card.custom.textureIntervalMax">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="strength" 
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        :precision="1" 
                        v-model="card.custom.textureStrength">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="rescale dist"
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        :precision="1" 
                        v-model="card.custom.textureRescaleDist">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="rescale time"
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        :precision="1" 
                        v-model="card.custom.textureRescaleTime">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-number-input
                        label="seed"
                        color="color1" 
                        variant="outlined" 
                        controlVariant="stacked"
                        :reverse="false"
                        :hideInput="false"
                        :inset="false" 
                        v-model="card.custom.seed">
                    </v-number-input>
                </div>

                <div class="expert-item">
                    <v-switch
                        inset
                        hide-details 
                        label="double-sided material" 
                        color="color1" 
                        density="compact"
                        v-model="card.custom.doubleSided">
                    </v-switch>
                </div>

            </SidePanelExpert>

        </template>

    </CardGenerate>
</template>

<script>
//import SUBSCRIPTIONS from '@generio-ai/library-legacy/constants/subscriptions'
import { mapGetters, mapActions } from '@generio-ai/library-legacy/plugins/vuex'
import { LayoutRowEntry } from '@generio-ai/library-legacy/components/layouts'
import { View } from '@generio-ai/library-legacy/stores/_api'
import QUALITY from '@generio-ai/library-legacy/constants/quality'
import CARD from '@/constants/card.js'
import OPTION from '@/constants/option.js'
import STRATEGY from '@/constants/strategy.js'
import AtomStatus from '@/components/common/atom/AtomStatus.vue'
import AtomText from '@/components/common/atom/AtomText.vue'
import CardGenerate from '@/components/flow/cards/CardGenerate.vue'
import OptionImage from '@/components/flow/options/OptionImage.vue'
import OptionIcon from '@/components/flow/options/OptionIcon.vue'
import SidePanelExpert from '@/components/flow/sides/SidePanelExpert.vue'
import SideTitle from '@/components/flow/sides/SideTitle.vue'

export default {
    name: 'CardGenerateTexture',
    components: {
        LayoutRowEntry,
        AtomStatus,
        CardGenerate,
        OptionImage,
        OptionIcon,
        AtomText,
        SidePanelExpert,
        SideTitle
    },
    props: {
        app: {
            type: String,
            required: true
        },
        card: {
            type: Object,
            required: true
        }
    },
    data() {
        return {
            healthy: {
                model_generate_texture: true,
                image_generate_texture: true
			},
            hovered: null
        }
    },
    computed: {
        ...mapGetters('auth', [
            'isAdministrator'
        ]),
        ...mapGetters('subscriptions', [
            'getSubscription'
        ]),
        ...mapGetters('flows', [
            'isExpertMode',
            'isActiveByIndex',
            'isLastByIndex',
            'getPrimaryCardForCard'
		]),

        keyTransparency() {
            return 'transparency'
        },
        keyTransparencyAllowed() {
            return true
        },
        keyTransparencyDisabled() {
            return false
        },
        keyTransparencyDefault() {
            return this.keyTransparencyDisabled
        },

        keyProjectionsAutomatic() {
            return 'automatic'
        },       
        keyProjectionsManual() {
            return 'custom'
        },
        keyProjectionsDefault() {
            return this.keyProjectionsAutomatic
        },

        keyVariantDefault() {
            return 'qwen'
        },

        keySequenceDefault() {
            return false
        },
        keyDelightDefault() {
            return false
        },
        keyFaceswapDefault() {
            return false
        },
        keyRefloorDefault() {
            return false
        },

        keyAlgorithmGenerio() {
            return 'generio'
        },
        keyAlgorithmXatlas() {
            return 'xatlas'
        },
        keyAlgorithmDefault() {
            return this.keyAlgorithmGenerio
        },

        keyDebugMaterialNone() {
            return 'none'
        },
        keyDebugMaterialUV() {
            return 'uv'
        },
        keyDebugMaterialStretch() {
            return 'stretch'
        },
        keyDebugMaterialCheckerboard() {
            return 'checkerboard'
        },
        keyDebugMaterialDefault() {
            return this.keyDebugMaterialCheckerboard
        },

        keyStrategies() {
            return Object.values(STRATEGY)
        },
        keyStrategyDefault() {
            return STRATEGY.NONE.value
        },

        hasPrivileges() {
            return true
        },

        hasProjectionsAutomatic() {
            return true
        },
        hasProjectionsManual() {
            return true
        },

        isOutage() {
            return !this.healthy.model_generate_texture && !this.healthy.image_generate_texture
		},
        isDegraded() {
            return !this.healthy.model_generate_texture || !this.healthy.image_generate_texture
        },

        isHealthyNative() {
            return this.healthy.model_generate_texture
        },
        isHealthyProject() {
            return this.healthy.image_generate_texture
        },
        isHealthyHybrid() {
            return this.healthy.model_generate_texture && this.healthy.image_generate_texture
        },

        isActiveCard() {
            return this.isActiveByIndex(this.app, this.index)
        },
        isLastCard() {
			return this.isLastByIndex(this.app, this.index)
		},

        statusLevel() {
            if (this.isOutage) return 'error'
            if (this.isDegraded) return 'warning'
            return 'healthy'
        },
        statusMessage() {
            if (this.isOutage) return "Model texture generation service is currently unavailable."
            if (this.isDegraded) return "Model texture generation service experiences a partial outage."
            return "Model texture generation service is healthy."
        },

        index() {
            return this.card.index
        },
        nextIndex() {
            return this.index + 1
        },
        selected() {
            return this.card.option
        },

        primaryCard() {
            return this.getPrimaryCardForCard(this.app, this.card, false)
        },
        primaryAssets() {
            return this.primaryCard?.assets || []
        },

        prompt() {
            return this.card?.prompt
        },
        promptQuality() {
            return this.prompt?.quality || QUALITY.DEFAULT
        },
        promptSolid() {
            return this.prompt?.solid ?? true
        },

        custom() {
            return this.card?.custom || {}
        },
        customTransparency() {
            return this.custom.transparency ?? this.keyTransparencyDefault
        },
        customProjections() {
            return this.custom.projections ?? this.keyProjectionsDefault
        },
        customViewsRaw() {
            return Array.isArray(this.custom.views) ? this.custom.views : null
        },
        customViews() {
            if (this.customProjections !== this.keyProjectionsManual) return null
            if (!this.customViewsRaw || this.customViewsRaw.length === 0) return null

            return this.customViewsRaw.map(view =>
                view instanceof View ? view : View.fromJSON(view)
            )
        },
        customVariant() {
            return this.custom.variant ?? this.keyVariantDefault
        },
        customSequence() {
            return this.custom.sequence ?? this.keySequenceDefault
        },
        customDelight() {
            return this.custom.delight ?? this.keyDelightDefault
        },
        customFaceswap() {
            return this.custom.faceswap ?? this.keyFaceswapDefault
        },
        customRefloor() {
            return this.custom.refloor ?? this.keyRefloorDefault
        },
        customSeed() {
            return this.custom.seed ?? this.keySeedDefault
        },
        customParameters() {
            return {
                'delight': this.customDelight,
                'faceswap': this.customFaceswap,
                'refloor': this.customRefloor,
                // mapping settings
                'mapping_xatlas_only': this.custom?.algo == this.keyAlgorithmXatlas ? true : false,
                'mapping_merge_distance': this.custom?.mergeDistance,
                'mapping_face_limit': this.custom?.faceLimit,
                'mapping_debug_material': this.custom?.debugMaterial,
                // texture settings
                'texture_multi_image_strategy': this.custom?.multiImageStrategy?.value,
                'texture_voxel_resolution': this.custom?.voxelResolution,
                'texture_resolution': this.custom?.textureResolution,
                'texture_steps': this.custom?.textureSteps,
                'texture_guidance_interval': [
                    this.custom?.textureIntervalMin,
                    this.custom?.textureIntervalMax,
                ],
                'texture_guidance_strength': this.custom?.textureStrength,
                'texture_guidance_rescale_dist': this.custom?.textureRescaleDist,
                'texture_guidance_rescale_time': this.custom?.textureRescaleTime,
                'texture_doubled_sided': this.custom?.doubledSided
            }
        },

        getVoxelResolutionForQuality() {
            if (this.promptQuality === QUALITY.ULTRA || this.promptQuality === QUALITY.HIGH)
                return 1024
            if (this.promptQuality === QUALITY.LOW || this.promptQuality === QUALITY.LEGACY)
                return 512
            return 1024
        },
        getTextureResolutionForQuality() {
            if (this.promptQuality === QUALITY.ULTRA)
                return 4096
            if (this.promptQuality === QUALITY.HIGH)
                return 2048
            if (this.promptQuality === QUALITY.LOW || this.promptQuality === QUALITY.LEGACY)
                return 1024
            return 2048
        },

        options() {
            return [
                {
                    index: 1,
                    beta: false,
                    selectable: this.isHealthyNative || this.isAdministrator,
                    ...OPTION.GENERATE_TEXTURE_NATIVE,
                },
                {
                    index: 2, 
                    beta: false, 
                    selectable: this.isHealthyProject || this.isAdministrator,
                    ...OPTION.GENERATE_TEXTURE_PROJECT,
                },
                {
                    index: 3,
                    beta: false,
                    selectable: this.isHealthyHybrid || this.isAdministrator,
                    ...OPTION.GENERATE_TEXTURE_HYBRID,
                }
            ]
        },
        guidance() {
            //if (!this.hasPrivileges) 
            //    return "Enhanced texture generation is available with a premium or higher subscription."

            switch (this.hovered) 
            {
                case this.keyTransparency:
                    return "Allows to generate textures with transparency (if possible)."

                case this.keyTransparencyAllowed:
                    return "Allows to generate textures with transparency (if possible)."
                case this.keyTransparencyDisabled:
                    return "Disables transparency in generated textures."

                case this.keyProjectionsAutomatic:
                    return "Automatically determines the views for the texture projection."
                case this.keyProjectionsManual:
                    return "Allows to manually configure the views for the texture projection."

                case OPTION.GENERATE_TEXTURE_NATIVE.id: 
                    return OPTION.GENERATE_TEXTURE_NATIVE.guidance + this.timeNative
                case OPTION.GENERATE_TEXTURE_PROJECT.id:
                    return OPTION.GENERATE_TEXTURE_PROJECT.guidance + this.timeProject
                case OPTION.GENERATE_TEXTURE_HYBRID.id:
                    return OPTION.GENERATE_TEXTURE_HYBRID.guidance + this.timeHybrid
            }
            return ''
        },

        timeNative() {
            return " (~60 seconds)."
        },
        timeProject() {
            // todo calculate if manual
            return " (~120 seconds)."
        },
        timeHybrid() {
            // todo calculate if manual
            return " (~180 seconds)."
        }
    },
    methods: {
        ...mapActions('dialogs', [
			'open'
        ]),
        ...mapActions('publics', [
			'fetchStatus'
		]),
        ...mapActions('flows', [
            'removeCard',
            'newTransaction',
            'commitPrimitiveAsset',
            'fetchGenerateModel',
        ]),
        async updateStatus() 
        {
            // default health status
            this.healthy = {
                model_generate_texture: true,
                image_generate_texture: true
			}

            try
            {
                // get status
                const status = await this.fetchStatus()

                // has healthy services
				if (status?.healthy) 
				{
                    // model_generate_texture
                    const modelGenerateTexture = status.healthy['model_generate_texture']
                    if (modelGenerateTexture === false) this.healthy.model_generate_texture = false

                    // image_generate_texture
                    const imageGenerateTexture = status.healthy['image_generate_texture']
                    if (imageGenerateTexture === false) this.healthy.image_generate_texture = false
				}
            }
            catch (error)
            {
                // silent fail - if status check does not work, we still want to proceed
            }
        },
        handleInit(assets, isNew) {
            if (isNew) {
                this.card.custom ??= {}

                // card settings
                this.card.custom.transparency ??= this.keyTransparencyDefault

                // dialog settings
                this.card.custom.projections ??= this.keyProjectionsDefault
                this.card.custom.views ??= null
                this.card.custom.variant ??= this.keyVariantDefault
                this.card.custom.sequence ??= this.keySequenceDefault
                this.card.custom.delight ??= this.keyDelightDefault
                this.card.custom.faceswap ??= this.keyFaceswapDefault
                this.card.custom.refloor ??= this.keyRefloorDefault

                // expert settings: mapping
                this.card.custom.algo ??= this.keyAlgorithmDefault
                this.card.custom.mergeDistance ??= 0.0001
                this.card.custom.faceLimit ??= 500_000
                this.card.custom.xatlasOnly ??= false
                this.card.custom.debugMaterial ??= this.keyDebugMaterialDefault

                // expert settings: texturing
                this.card.custom.multiImageStrategy ??= this.keyStrategyDefault
                this.card.custom.voxelResolution ??= this.getVoxelResolutionForQuality
                this.card.custom.textureResolution ??= this.getTextureResolutionForQuality
                this.card.custom.textureSteps ??= 12
                this.card.custom.textureIntervalMin ??= 0.6
                this.card.custom.textureIntervalMax ??= 0.9
                this.card.custom.textureStrength ??= 1
                this.card.custom.textureRescaleDist ??= 0
                this.card.custom.textureRescaleTime ??= 3.0
                this.card.custom.seed ??= -1
                this.card.custom.doubleSided ??= true
            }
        },
        handleHover(option) {
            this.hovered = option?.id
        },
        handleAutomatic() {
            this.card.custom ??= {}
            this.card.custom.projections = this.keyProjectionsAutomatic
            this.card.custom.views = null
            this.handleClick()
        },
        handleManual() {
            this.card.custom ??= {}
            this.card.custom.projections = this.keyProjectionsManual

            if (!Array.isArray(this.card.custom.views) || this.card.custom.views.length === 0) {
                this.card.custom.views = [new View(
                    0,
                    0,
                    0,
                    1,
                    null,
                    null,
                    null,
                    null,
                    null
                )]
            }
            this.handleClick()
            this.open({
                component: 'projection',
                payload: {
                    card: this.card
                }
            })
        },
        handleClick() {
            this.$refs.card?.reset()
			this.removeCard({
				app: this.app, 
				index: this.nextIndex
			})
        },
        handleOption(option) {
            this.card.option = option.id

            switch(option.id) 
            {
                case OPTION.GENERATE_TEXTURE_NATIVE.id:
                    this.newTransaction({
                        app: this.app,
                        card: this.card,
                        taskCommit: this.commitPrimitiveAsset(),
                        taskFetch: this.fetchGenerateModel({
                            component: CARD.MODEL,
                            quality: this.promptQuality,
                            solid: this.promptSolid,
                            texture: true,
                            textureMode: 'native',
                            textureTransparency: this.customTransparency,
                            seed: this.customSeed,
                        })
                    })
                    break

                case OPTION.GENERATE_TEXTURE_PROJECT.id:
                    this.newTransaction({
                        app: this.app,
                        card: this.card,
                        taskCommit: this.commitPrimitiveAsset(),
                        taskFetch: this.fetchGenerateModel({
                            component: CARD.MODEL,
                            quality: this.promptQuality,
                            solid: this.promptSolid,
                            texture: true,
                            textureMode: 'project',
                            textureTransparency: this.customTransparency,
                            textureViews: this.customViews,
                            textureSequence: this.customSequence,
                            variantImageGenerate: this.customVariant,
                            seed: this.customSeed,
                            parameters: this.customParameters
                        })
                    })
                    break

                case OPTION.GENERATE_TEXTURE_HYBRID.id:
                    this.newTransaction({
                        app: this.app,
                        card: this.card,
                        taskCommit: this.commitPrimitiveAsset(),
                        taskFetch: this.fetchGenerateModel({
                            component: CARD.MODEL,
                            quality: this.promptQuality,
                            solid: this.promptSolid,
                            texture: true,
                            textureMode: 'hybrid',
                            textureTransparency: this.customTransparency,
                            textureViews: this.customViews,
                            textureSequence: this.customSequence,
                            variantImageGenerate: this.customVariant,
                            seed: this.customSeed,
                            parameters: this.customParameters
                        })
                    })
                    break
            }
        },
        handleKeyboardShortcuts(event) {

            // only if the card is active
            if (!this.isActiveCard) return

            // ignore keyboard shortcuts if the focus is on an input, textarea, select, or contenteditable element
            const target = event.target
            if (
                target instanceof HTMLInputElement ||
                target instanceof HTMLTextAreaElement ||
                target instanceof HTMLSelectElement ||
                (target instanceof HTMLElement && target.isContentEditable)
            ) {
                return
            }

            // get the pressed key
            const key = event.key
            // check if the key is a single digit (1-9)
            if (/^[1-9]$/.test(key)) {
                // convert to 0-indexed position
                const index = parseInt(key, 10) - 1
                // if an option exists at that index, trigger it
                if (index >= 0 && index < this.options.length) {
                    event.preventDefault()
                    this.handleOption(this.options[index])
                }
            }
        }
    },
    mounted() {
        this.updateStatus()
		window.addEventListener('keydown', this.handleKeyboardShortcuts)
	},
	beforeUnmount() {
        window.removeEventListener('keydown', this.handleKeyboardShortcuts)
    }
}
</script>

<style>
.card-generate-texture-pre .entry .second {
    width: 100%;
}

.card-generate-texture-pre .entry .toggle .v-btn.v-btn--disabled {
    opacity: 0.5;
}
</style>

<style scoped>
.card-generate-texture-pre {
    width: 100%;
    padding-left: 30px;
	padding-right: 30px;
    margin-top: 30px;
    margin-bottom: 30px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 15px;
}

.card-generate-texture-pre .entry {
    width: 100%;
    max-width: 650px;
}

.card-generate-texture-pre .entry .label-inactive {
    color: rgb(var(--v-theme-tertiary)) !important;
}

.card-generate-texture-pre .entry .toggle {
    display: flex;
    width: 100%;
}

.card-generate-texture-pre .entry .toggle-button {
    flex: 1;
}

.card-generate-texture .toggle {
    max-height: 40px !important;
    min-height: 40px !important;
	border: 1px solid rgb(var(--v-theme-lightbase));
}

.card-generate-texture .toggle-button {
    max-height: 38px !important;
    min-height: 38px !important;
	background-color: rgb(var(--v-theme-darkbase));
    border-radius: 0px !important;
}

.card-generate-texture .toggle-button:not(:first-child) {
    border-left: 1px solid rgb(var(--v-theme-lightbase));
}

@media (max-width: 800px) {
    .card-generate-texture-pre {
        padding-left: 15px;
        padding-right: 15px;
        margin-top: 20px;
        margin-bottom: 20px;
        gap: 10px;
    }
}

@media (max-width: 600px) {
    .card-generate-texture-pre {
        margin-top: 15px;
        margin-bottom: 15px;
    }

    .card-generate-texture-pre .entry {
        width: 100%;
        max-width: unset;
        gap: 3px !important;
    }

    .card-generate-texture .toggle {
        max-height: 35px !important;
        min-height: 35px !important;
    }

    .card-generate-texture .toggle-button {
        max-height: 33px !important;
        min-height: 33px !important;
    }
}

@media (max-width: 400px) {
    .card-generate-texture-pre {
        margin-top: 10px;
        margin-bottom: 10px;
    }
}
</style>
